import { useCallback, useEffect, useRef, useState } from "react";
import { DDZ_INVITE_PATH, readInviteContext, inviteLocation } from "./DDZInvite";
import { installDDZInviteBridge } from "./DDZInviteBridge";
import { hasRoomInviteFields, roomInviteHomePath, ROOM_INVITE_REGISTRY } from "./RoomInvite";

// The APK can enqueue immediately; only navigation waits for account hydration.
export function useDDZInviteBridge({ location, history, loaded, busy, registry = ROOM_INVITE_REGISTRY }) {
  const controller = useRef(null);
  if (!controller.current && typeof window !== "undefined") controller.current = installDDZInviteBridge(window, { registry });
  const [pending, setPending] = useState(() => controller.current ? controller.current.pending() : null);
  useEffect(() => {
    const bridge = controller.current;
    if (!bridge) return;
    const unsubscribe = bridge.subscribe(setPending);
    setPending(bridge.pending());
    return unsubscribe;
  }, []);

  useEffect(() => {
    const bridge = controller.current;
    const current = bridge && bridge.pending();
    if (!loaded || !current) return;
    const home = ["/home", "/"].includes(location.pathname);
    const roomPath = roomInviteHomePath(location.search, registry);
    const routeContext = readInviteContext(location.search);
    const routePath = routeContext && inviteLocation(routeContext.inviteToken, routeContext.attribution);
    const superseded = bridge.isSupersededPath(routePath || roomPath);
    if (current.kind === "room") {
      // A new delivery matching an old game URL must first be routed by this delivery.
      if (current.phase === "routing") {
        const query = new URLSearchParams(location.search);
        // Authentication owns its existing back_to while this durable intent survives a cold restart.
        if (location.pathname === "/game" && query.getAll("game_id").length === 1 &&
            query.get("game_id") === current.gameId && roomPath === current.path) {
          bridge.finishRoom(current.deliveryId, current.path);
        }
        return;
      }
      // The App's registry-driven router starts only after this gate marks the pending room routed.
      if (busy || (!home && !(location.pathname === DDZ_INVITE_PATH && superseded))) return;
      if (roomPath === current.path) { bridge.markRouted(current.deliveryId); return; }
      if (hasRoomInviteFields(location.search, registry) && !superseded) return;
      if (!bridge.markRouted(current.deliveryId)) return;
      try { history.replace(current.path); }
      catch (_) { bridge.retryRoute(current.deliveryId); }
      return;
    }
    if (current.phase === "routing") return;
    if ([DDZ_INVITE_PATH, "/home", "/"].includes(location.pathname) && routePath === current.path) {
      bridge.markRouted(current.deliveryId);
      return;
    }
    // Keep payments, an active game, auth pages and other invitations in place.
    if (busy || (!home && !(location.pathname === DDZ_INVITE_PATH && superseded)) ||
        ((routeContext || hasRoomInviteFields(location.search, registry)) && !superseded)) return;
    if (!bridge.markRouted(current.deliveryId)) return;
    try { history.replace(current.path); }
    catch (_) { bridge.retryRoute(current.deliveryId); }
  }, [loaded, busy, location.pathname, location.search, location.key, pending, history, registry]);

  const finish = useCallback((inviteToken, expectedPath) => controller.current &&
    controller.current.finish(inviteToken, expectedPath), []);
  const routeContext = readInviteContext(location.search);
  const routePath = routeContext ? inviteLocation(routeContext.inviteToken, routeContext.attribution) :
    roomInviteHomePath(location.search, registry);
  const superseded = controller.current && controller.current.isSupersededPath(routePath);
  return { pending, finish, superseded };
}

export default useDDZInviteBridge;
